import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { format, parseISO } from "date-fns";
import { debounce } from "lodash";
import { FunctionComponent, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircylCrmContext, useApiMutation } from "../hooks/useApi";
import { TimeSheet, TimeSheetDay } from "../types/timesheet";
import Timesheet from "./Timesheet";

interface Props {
  handleAmendClose: () => void;
  amendOpen: boolean;
  timesheet: TimeSheet;
  onAmendSuccess: () => void;
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const AmendTimesheetDialog: FunctionComponent<Props> = (
  props: Props
): JSX.Element => {
  const { amendOpen, handleAmendClose, timesheet, onAmendSuccess } = props;
  const { apiUpdate } = useApiMutation();
  const [days] = useState<TimeSheetDay[]>(timesheet.days);
  const [amendedDays, setAmendedDays] = useState<TimeSheetDay[]>([
    ...timesheet.days,
  ]);
  const [daysChanges, setDaysChanges] = useState<boolean[]>([
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ]);
  const [reason, setReason] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(true);
  const { setButtonsLocked } = useContext(CircylCrmContext);

  const { t } = useTranslation();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (day: TimeSheetDay, index: number) => {
    const checkForChange = (day: TimeSheetDay) => {
      if (day.type !== days[index].type) {
        return true;
      } else if (day.type === 0) {
        if (
          day.hour !== days[index].hour ||
          day.minute !== days[index].minute
        ) {
          return true;
        }
      }
      return false;
    };

    setAmendedDays((prevAmendedDays) => {
      const newAmendedDays = [...prevAmendedDays];
      newAmendedDays[index] = day;
      return newAmendedDays;
    });

    setDaysChanges((prevDaysChanges) => {
      const newDaysChanges = [...prevDaysChanges];
      newDaysChanges[index] = checkForChange(day);
      return newDaysChanges;
    });
  };

  const isDisabled = daysChanges.every((change) => !change);

  const handleAmendSave = () => {
    setButtonsLocked(true);
    apiUpdate.mutate(
      {
        entity: "timesheets",
        post: {
          id: timesheet?.id,
          amendedDays,
          days: timesheet.days,
          status: 3,
          comments: reason,
          clientName: timesheet.clientName,
        },
      },
      {
        onSuccess: () => {
          onAmendSuccess();
        },
        onError: (error) => console.error(error),
      }
    );
  };

  return (
    <BootstrapDialog
      onClose={() => {
        handleAmendClose();
        setIsDialogOpen(false);
      }}
      aria-labelledby="customized-dialog-title"
      open={amendOpen}
      fullWidth={isSmallScreen}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {t("dialogs.amendDialog.title")}
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={() => {
          handleAmendClose();
          setIsDialogOpen(false);
        }}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          {!isSmallScreen ? (
            <>
              <Grid item xs={6}>
                <Stack direction={"row"} padding={2} spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <PersonIcon
                      fontSize="large"
                      sx={{ color: "rgba(0, 0, 0, 0.5)" }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2">
                      {" "}
                      {timesheet.candidate}{" "}
                    </Typography>
                    <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
                      {" "}
                      {t("tsTable.candidate")}{" "}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={6}>
                <Stack direction={"row"} padding={2} spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <DateRangeIcon
                      fontSize="large"
                      sx={{ color: "rgba(0, 0, 0, 0.5)" }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2">
                      {" "}
                      {format(
                        parseISO(timesheet.weekEnding),
                        "dd/MM/yyyy"
                      )}{" "}
                    </Typography>
                    <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
                      {" "}
                      {t("tsTable.weekEnding")}{" "}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={6}>
                <Stack direction={"row"} padding={2} spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <PersonIcon
                      fontSize="large"
                      sx={{ color: "rgba(0, 0, 0, 0.5)" }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2">
                      {" "}
                      {timesheet.bookingContact}{" "}
                    </Typography>
                    <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
                      {" "}
                      {t("tsTable.bookingContact")}{" "}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={6}>
                <Stack direction={"row"} padding={2} spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <PersonIcon
                      fontSize="large"
                      sx={{ color: "rgba(0, 0, 0, 0.5)" }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2">
                      {" "}
                      {timesheet.owningConsultant}{" "}
                    </Typography>
                    <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
                      {" "}
                      {t("tsTable.owningConsultant")}{" "}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={6}>
                <Stack direction={"row"} padding={2} spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <SchoolIcon
                      fontSize="large"
                      sx={{ color: "rgba(0, 0, 0, 0.5)" }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2">
                      {" "}
                      {timesheet.jobTitle}{" "}
                    </Typography>
                    <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
                      {" "}
                      {t("tsTable.jobDescription")}{" "}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={6}>
                <Stack direction={"row"} padding={2} spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <BusinessIcon
                      fontSize="large"
                      sx={{ color: "rgba(0, 0, 0, 0.5)" }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2">
                      {" "}
                      {timesheet.location}{" "}
                    </Typography>
                    <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
                      {" "}
                      {t("tsTable.location")}{" "}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" textAlign="center">
                  {" "}
                  {t("tsTable.candidate")}: {timesheet.candidate}{" "}
                </Typography>
              </Box>
            </Grid>
          )}

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mt: "20px",
              mb: "20px",
            }}
          >
            <Timesheet
              days={days}
              variant={isSmallScreen ? "small" : "large"}
              onChange={handleChange}
              timesheet={timesheet}
              isDialogOpen={isDialogOpen}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mt: "20px",
              mb: "20px",
            }}
          >
            <TextField
              label={t("dialogs.amendDialog.reason")}
              variant="outlined"
              size={isSmallScreen ? "small" : "medium"}
              fullWidth
              onChange={debounce(
                (event: React.ChangeEvent<HTMLInputElement>) => {
                  setReason(event.target.value);
                },
                500
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          autoFocus
          onClick={() => {
            handleAmendClose();
            setIsDialogOpen(false);
          }}
        >
          {t("buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleAmendSave}
          disabled={isDisabled}
        >
          {t("buttons.submitApprove")}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
};

export default AmendTimesheetDialog;
